/**
 * Stock Alerts Display — Technical and Fundamental alerts for NGX stocks
 *
 * Displays all NGX stock alerts with filtering by:
 * - Alert type (Technical/Fundamental)
 * - Severity (Critical/High/Medium/Low)
 * - Individual symbols
 */

import { useState, useMemo } from 'react';
import {
  getAllStockAlerts,
  getAlertsByType,
  getAlertsBySeverity,
  getCriticalAlerts,
  getHighSeverityAlerts,
  type AlertType,
  type AlertSeverity,
} from '../lib/ngx-stock-alerts';

interface FilterOptions {
  type: AlertType | 'all';
  severity: AlertSeverity | 'all';
  symbol: string;
}

const severityColor = (severity: AlertSeverity): string => {
  switch (severity) {
    case 'critical': return '#dc2626';
    case 'high': return '#f97316';
    case 'medium': return '#eab308';
    case 'low': return '#3b82f6';
    default: return '#6b7280';
  }
};

const severityLabel = (severity: AlertSeverity): string => {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
};

export function StockAlerts() {
  const [filters, setFilters] = useState<FilterOptions>({
    type: 'all',
    severity: 'all',
    symbol: '',
  });

  const allAlerts = useMemo(() => getAllStockAlerts(), []);

  const uniqueSymbols = useMemo(() => {
    const symbols = new Set(allAlerts.map((a) => a.symbol));
    return Array.from(symbols).sort();
  }, [allAlerts]);

  const filteredAlerts = useMemo(() => {
    let filtered = allAlerts;

    if (filters.type !== 'all') {
      filtered = filtered.filter((a) => a.type === filters.type);
    }

    if (filters.severity !== 'all') {
      filtered = filtered.filter((a) => a.severity === filters.severity);
    }

    if (filters.symbol) {
      filtered = filtered.filter((a) => a.symbol === filters.symbol);
    }

    return filtered.sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aOrder = severityOrder[a.severity];
      const bOrder = severityOrder[b.severity];
      return aOrder - bOrder;
    });
  }, [allAlerts, filters]);

  const criticalCount = getCriticalAlerts().length;
  const highCount = getHighSeverityAlerts().length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>
      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#1a1f2e',
        borderBottom: '1px solid #333'
      }}>
        <div style={{
          backgroundColor: '#252d3d',
          border: `2px solid ${severityColor('critical')}`,
          borderRadius: '8px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '11px', color: '#999' }}>CRITICAL</div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: severityColor('critical'),
            marginTop: '4px'
          }}>
            {criticalCount}
          </div>
        </div>

        <div style={{
          backgroundColor: '#252d3d',
          border: `2px solid ${severityColor('high')}`,
          borderRadius: '8px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '11px', color: '#999' }}>HIGH</div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: severityColor('high'),
            marginTop: '4px'
          }}>
            {highCount}
          </div>
        </div>

        <div style={{
          backgroundColor: '#252d3d',
          border: `2px solid ${severityColor('medium')}`,
          borderRadius: '8px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '11px', color: '#999' }}>MEDIUM</div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: severityColor('medium'),
            marginTop: '4px'
          }}>
            {getAlertsBySeverity('medium').length}
          </div>
        </div>

        <div style={{
          backgroundColor: '#252d3d',
          border: `2px solid ${severityColor('low')}`,
          borderRadius: '8px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '11px', color: '#999' }}>LOW</div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: severityColor('low'),
            marginTop: '4px'
          }}>
            {getAlertsBySeverity('low').length}
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#252d3d',
        borderBottom: '1px solid #333',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#999' }}>Filter:</div>

        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value as AlertType | 'all' })}
          style={{
            padding: '6px 12px',
            backgroundColor: '#1a1f2e',
            color: '#fff',
            border: '1px solid #333',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Types</option>
          <option value="technical">Technical</option>
          <option value="fundamental">Fundamental</option>
        </select>

        <select
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value as AlertSeverity | 'all' })}
          style={{
            padding: '6px 12px',
            backgroundColor: '#1a1f2e',
            color: '#fff',
            border: '1px solid #333',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          value={filters.symbol}
          onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
          style={{
            padding: '6px 12px',
            backgroundColor: '#1a1f2e',
            color: '#fff',
            border: '1px solid #333',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          <option value="">All Symbols</option>
          {uniqueSymbols.map((sym) => (
            <option key={sym} value={sym}>
              {sym}
            </option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>
          Showing {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Alerts List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: '12px',
        alignContent: 'start'
      }}>
        {filteredAlerts.length === 0 ? (
          <div style={{
            gridColumn: '1 / -1',
            padding: '60px 24px',
            textAlign: 'center',
            color: '#666'
          }}>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>No alerts match the selected filters</div>
            <div style={{ fontSize: '12px', color: '#555' }}>Try adjusting your filter criteria</div>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              style={{
                backgroundColor: '#252d3d',
                border: `1px solid ${severityColor(alert.severity)}`,
                borderLeft: `4px solid ${severityColor(alert.severity)}`,
                borderRadius: '6px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    marginBottom: '2px'
                  }}>
                    {alert.symbol}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#999'
                  }}>
                    {alert.name}
                  </div>
                </div>
                <div style={{
                  padding: '4px 8px',
                  backgroundColor: severityColor(alert.severity),
                  color: alert.severity === 'low' ? '#000' : '#fff',
                  borderRadius: '3px',
                  fontSize: '10px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>
                  {severityLabel(alert.severity)}
                </div>
              </div>

              {/* Type Badge */}
              <div style={{
                display: 'inline-flex',
                width: 'fit-content',
                padding: '4px 8px',
                backgroundColor: alert.type === 'technical' ? '#1e3a8a' : '#4c0519',
                color: alert.type === 'technical' ? '#93c5fd' : '#fbcfe8',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: 600
              }}>
                {alert.type === 'technical' ? '📊 Technical' : '📈 Fundamental'}
              </div>

              {/* Title */}
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#fff'
              }}>
                {alert.title}
              </div>

              {/* Description */}
              <div style={{
                fontSize: '12px',
                color: '#ccc',
                lineHeight: '1.4'
              }}>
                {alert.description}
              </div>

              {/* Metric */}
              {alert.metric && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#1a1f2e',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  <div>
                    <div style={{ color: '#999', marginBottom: '4px' }}>Metric</div>
                    <div style={{ color: '#fff', fontWeight: 600 }}>{alert.metric}</div>
                  </div>
                  {alert.current !== undefined && (
                    <div>
                      <div style={{ color: '#999', marginBottom: '4px' }}>Current</div>
                      <div style={{ color: '#fff', fontWeight: 600 }}>
                        {typeof alert.current === 'number' && alert.current > 100
                          ? alert.current.toLocaleString('en-NG', { maximumFractionDigits: 0 })
                          : alert.current.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {alert.threshold !== undefined && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ color: '#999', marginBottom: '4px' }}>Threshold/Target</div>
                      <div style={{ color: '#fff', fontWeight: 600 }}>
                        {typeof alert.threshold === 'number' && alert.threshold > 100
                          ? alert.threshold.toLocaleString('en-NG', { maximumFractionDigits: 0 })
                          : alert.threshold.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Timestamp */}
              <div style={{
                fontSize: '11px',
                color: '#666',
                borderTop: '1px solid #333',
                paddingTop: '8px'
              }}>
                Alert generated: {new Date(alert.timestamp).toLocaleString('en-NG')}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default StockAlerts;
